import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Eye, EyeOff, Target, TrendingUp, BarChart3, FileSearch, Users2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LanguageToggle from "@/components/LanguageToggle";

const HERO_IMG = "https://images.unsplash.com/photo-1556983852-43bf21186b2a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

// Portada comercial (nueva, WOW/dinámica) · spotlight rotativo de las
// capacidades del cockpit comercial — reemplaza la lista estática anterior
// para dar sensación de movimiento sin exponer cifras de negocio reales
// pre-login (pipeline/€/hit rate son datos sensibles de la compañía).
const SPOTLIGHT = [
  { icon: Target,     title: "Detección diaria de licitaciones", desc: "Pando IA rastrea PLACSP y portales regionales cada madrugada, sin que nadie tenga que mirar uno a uno." },
  { icon: TrendingUp, title: "Pipeline comercial unificado", desc: "Oportunidad → Oferta → Comité → Adjudicación, en un único recorrido con trazabilidad de cada decisión." },
  { icon: BarChart3,  title: "Hit Rate y seguimiento económico", desc: "Rentabilidad, tarifas y avance de ofertas con la fuente y fecha de cada dato, nunca una cifra inventada." },
  { icon: FileSearch, title: "Análisis de pliegos con IA", desc: "Extracción de requisitos, plazos y condiciones económicas directamente del PCAP/PPT de cada expediente." },
  { icon: Users2,      title: "Delegados y comité en red", desc: "Alertas regionales, digest automático y checklist de implantación TIC conectados al mismo expediente." },
];

export default function Login() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [spotlightIdx, setSpotlightIdx] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  // Spotlight comercial dinámico — rota cada 4.2s, pausa si el usuario
  // interactúa con los puntos de navegación manualmente.
  useEffect(() => {
    const iv = setInterval(() => setSpotlightIdx(i => (i + 1) % SPOTLIGHT.length), 4200);
    return () => clearInterval(iv);
  }, []);

  // Iter279 · feedback persistente tras el auto-logout por token caducado/revocado
  // (antes se perdía el aviso al navegar; ahora se explica por qué llegó aquí).
  useEffect(() => {
    if (new URLSearchParams(location.search).get("expired") === "1") {
      setError("Tu sesión anterior ha caducado o fue revocada. Vuelve a iniciar sesión — si estabas escribiendo algo sin guardar, lamentablemente se perdió.");
    }
  }, [location.search]);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Error al iniciar sesión");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, form);
      setSuccess("Registro enviado. El administrador recibirá una notificación para aprobar tu acceso.");
      setTab("login");
    } catch (err) {
      setError(err.response?.data?.detail || "Error en el registro");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:border-[#007934] focus:ring-2 focus:ring-[#007934]/20 transition-shadow";

  return (
    <div className="min-h-screen flex">
      {/* Left panel — portada comercial WOW/dinámica */}
      <div className="hidden lg:flex lg:w-[46%] relative overflow-hidden flex-col justify-between p-12" style={{ background: "#022815" }} data-testid="login-commercial-hero">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.22]" />
        {/* Mesh de gradientes radiales animado — sensación de movimiento sin recurrir a un único gradiente oscuro plano */}
        <div
          className="absolute inset-0 mesh-drift"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 20%, rgba(16,185,129,0.35), transparent 60%), radial-gradient(circle at 85% 75%, rgba(0,121,52,0.4), transparent 55%), radial-gradient(circle at 50% 100%, rgba(2,40,21,0.9), transparent 70%)",
          }}
        />
        <div className="absolute inset-0 grain-overlay" />
        {/* Orbes de cristal flotantes — profundidad */}
        <div className="absolute -top-10 right-10 w-56 h-56 rounded-full bg-emerald-400/10 border border-white/10 backdrop-blur-2xl float-orb" />
        <div className="absolute bottom-20 -left-16 w-72 h-72 rounded-full bg-teal-300/10 border border-white/10 backdrop-blur-2xl float-orb-slow" />

        <div className="relative z-10">
          <img src="/valoriza-logo.svg" alt="Valoriza" className="h-10 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
          <p className="text-emerald-200/80 text-[10px] tracking-[0.3em] uppercase mt-2 font-medium">Cockpit Comercial</p>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-white text-3xl xl:text-4xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
            {t("login.platformName")}
          </h1>
          <p className="text-emerald-100/80 text-sm mt-3 leading-relaxed">{t("login.platformTagline")}</p>

          {/* Spotlight rotativo */}
          <div className="mt-8 min-h-[104px]" data-testid="login-hero-spotlight">
            <AnimatePresence mode="wait">
              {(() => {
                const F = SPOTLIGHT[spotlightIdx];
                return (
                  <motion.div
                    key={spotlightIdx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="bg-white/[0.08] backdrop-blur-md border border-white/10 rounded-xl px-4 py-3.5 flex items-start gap-3"
                  >
                    <div className="w-9 h-9 rounded-lg bg-emerald-400/20 flex items-center justify-center flex-shrink-0">
                      <F.icon size={16} className="text-emerald-300" />
                    </div>
                    <div>
                      <p className="text-emerald-50 text-[13px] font-semibold">{F.title}</p>
                      <p className="text-emerald-100/70 text-[12px] mt-0.5 leading-snug">{F.desc}</p>
                    </div>
                  </motion.div>
                );
              })()}
            </AnimatePresence>
            <div className="flex items-center gap-1.5 mt-3.5">
              {SPOTLIGHT.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSpotlightIdx(i)}
                  data-testid={`login-hero-spotlight-dot-${i}`}
                  aria-label={`Ver capacidad ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === spotlightIdx ? "w-6 bg-emerald-300" : "w-1.5 bg-white/25 hover:bg-white/40"}`}
                />
              ))}
            </div>
          </div>
        </div>

        <p className="relative z-10 text-emerald-200/40 text-[11px]">© {new Date().getFullYear()} Valoriza Servicios Medioambientales</p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F8FAF9] relative">
        <div className="absolute top-4 right-4 flex items-center gap-2"><LanguageToggle /></div>
        <div className="w-full max-w-sm animate-fade-up">
          <div className="text-center mb-8">
            <div className="lg:hidden w-14 h-14 bg-[#007934] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-900/20">
              <span className="text-white font-black text-2xl" style={{ fontFamily: "Outfit" }}>V</span>
            </div>
            <h2 className="text-[26px] font-semibold tracking-tight text-gray-900" style={{ fontFamily: "Outfit" }}>
              {tab === "login" ? t("login.welcome") : t("login.tabSignup")}
            </h2>
            <p className="text-gray-500 text-sm mt-1.5">
              {tab === "login" ? t("login.subtitle") : t("login.signupSuccess")}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
            <button onClick={() => setTab("login")} data-testid="login-tab"
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${tab === "login" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {t("login.tabLogin")}
            </button>
            <button onClick={() => setTab("register")} data-testid="register-tab"
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${tab === "register" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {t("login.tabSignup")}
            </button>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl mb-4 animate-fade-up" data-testid="login-error">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl mb-4 animate-fade-up" data-testid="register-success">{success}</div>}

          {tab === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("login.email")}</label>
                <input name="email" type="email" value={form.email} onChange={handle} required
                  className={inputCls} placeholder={t("login.emailPlaceholder")} data-testid="email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("login.password")}</label>
                <div className="relative">
                  <input name="password" type={showPass ? "text" : "password"} value={form.password} onChange={handle} required
                    className={`${inputCls} pr-10`} placeholder={t("login.passwordPlaceholder")} data-testid="password-input" />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-[#007934] hover:bg-[#006528] text-white rounded-xl py-2.5 text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60 shadow-lg shadow-emerald-900/15 hover:shadow-emerald-900/25"
                data-testid="login-submit-btn">
                {loading ? t("login.submitting") : t("login.submit")}
              </button>
              <div className="text-center">
                <a href="/forgot-password"
                   className="text-xs text-slate-500 hover:text-[#007934] hover:underline transition"
                   data-testid="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </a>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("login.signupName")}</label>
                <input name="name" type="text" value={form.name} onChange={handle} required
                  className={inputCls} placeholder={t("common.name")} data-testid="name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("login.email")}</label>
                <input name="email" type="email" value={form.email} onChange={handle} required
                  className={inputCls} placeholder={t("login.emailPlaceholder")} data-testid="reg-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("login.password")}</label>
                <input name="password" type="password" value={form.password} onChange={handle} required
                  className={inputCls} placeholder={t("login.passwordPlaceholder")} data-testid="reg-password-input" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-[#007934] hover:bg-[#006528] text-white rounded-xl py-2.5 text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60 shadow-lg shadow-emerald-900/15"
                data-testid="register-submit-btn">
                {loading ? t("common.saving") : t("login.signupSubmit")}
              </button>
              <p className="text-xs text-gray-500 text-center">{t("login.signupSuccess")}</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
